// ═══════════════ RECURRING BOOKING — CLIENT-SIDE HELPERS (Batch 12) ═══════════════
// Helpers for the rider booking wizard's recurrence step.

import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

export const MAX_SERIES_WEEKS = 12;
export const MAX_OCCURRENCES_PER_SERIES = 84;

// Hardcoded fallback (matches server-side fallback). Server's config/nswPublicHolidays overrides this.
const DEFAULT_NSW_HOLIDAYS_FALLBACK = [
  '2026-01-01', '2026-01-26', '2026-04-03', '2026-04-04', '2026-04-05', '2026-04-06',
  '2026-04-25', '2026-06-08', '2026-10-05', '2026-12-25', '2026-12-26', '2026-12-28',
  '2027-01-01', '2027-01-26', '2027-03-29', '2027-04-02', '2027-04-03', '2027-04-04', '2027-04-05',
];

let _holidayCache = null;

export async function loadHolidays() {
  if (_holidayCache) return _holidayCache;
  try {
    const snap = await getDoc(doc(db, 'config', 'nswPublicHolidays'));
    if (snap.exists() && Array.isArray(snap.data().dates) && snap.data().dates.length > 0) {
      _holidayCache = snap.data().dates;
      return _holidayCache;
    }
  } catch (e) { /* fall through */ }
  _holidayCache = DEFAULT_NSW_HOLIDAYS_FALLBACK;
  return _holidayCache;
}

export function isHoliday(dateStr, holidays) {
  return holidays.includes(dateStr);
}

// Generate preview of occurrence dates from a pattern. Mirrors server-side logic
// so the rider sees the exact dates before submitting.
export function generateOccurrencePreview(pattern, holidays = []) {
  if (!pattern || !pattern.time) return { dates: [], error: 'Set a time' };
  if (!/^\d{2}:\d{2}$/.test(pattern.time)) return { dates: [], error: 'Time must be HH:MM' };

  const out = [];

  if (pattern.mode === 'weekly') {
    const { daysOfWeek, startDate, weeks } = pattern;
    if (!Array.isArray(daysOfWeek) || daysOfWeek.length === 0) {
      return { dates: [], error: 'Select at least one day of the week' };
    }
    if (!startDate || !/^\d{4}-\d{2}-\d{2}$/.test(startDate)) {
      return { dates: [], error: 'Pick a start date' };
    }
    if (typeof weeks !== 'number' || weeks < 1 || weeks > MAX_SERIES_WEEKS) {
      return { dates: [], error: `Weeks must be 1-${MAX_SERIES_WEEKS}` };
    }
    const start = new Date(`${startDate}T00:00:00`);
    if (isNaN(start.getTime())) return { dates: [], error: 'Invalid start date' };
    const end = new Date(start);
    end.setDate(end.getDate() + weeks * 7);
    for (const cursor = new Date(start); cursor < end; cursor.setDate(cursor.getDate() + 1)) {
      const dow = cursor.getDay();
      if (!daysOfWeek.includes(dow)) continue;
      const ds = cursor.toISOString().split('T')[0];
      if (pattern.skipHolidays && isHoliday(ds, holidays)) continue;
      out.push({ date: ds, time: pattern.time, scheduledAt: `${ds}T${pattern.time}:00` });
    }
  } else if (pattern.mode === 'custom') {
    const { dates } = pattern;
    if (!Array.isArray(dates) || dates.length === 0) return { dates: [], error: 'Pick at least one date' };
    if (dates.length > MAX_OCCURRENCES_PER_SERIES) {
      return { dates: [], error: `Maximum ${MAX_OCCURRENCES_PER_SERIES} occurrences` };
    }
    const horizon = new Date();
    horizon.setDate(horizon.getDate() + MAX_SERIES_WEEKS * 7);
    for (const d of dates) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return { dates: [], error: `Invalid date: ${d}` };
      const dObj = new Date(`${d}T00:00:00`);
      if (dObj < new Date()) continue;
      if (dObj > horizon) return { dates: [], error: `${d} is beyond the ${MAX_SERIES_WEEKS}-week horizon` };
      if (pattern.skipHolidays && isHoliday(d, holidays)) continue;
      out.push({ date: d, time: pattern.time, scheduledAt: `${d}T${pattern.time}:00` });
    }
  } else {
    return { dates: [], error: 'Pattern mode required' };
  }

  if (out.length === 0) return { dates: [], error: 'No valid occurrences (check days/dates/holidays)' };
  out.sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt));
  return { dates: out, error: null };
}

// Format helpers
export const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const DAY_NAMES_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function formatPattern(pattern) {
  if (!pattern) return 'One-off';
  if (pattern.mode === 'weekly') {
    const days = (pattern.daysOfWeek || []).map((d) => DAY_NAMES[d]).join('/');
    return `${days} for ${pattern.weeks} week${pattern.weeks > 1 ? 's' : ''} at ${pattern.time}`;
  }
  if (pattern.mode === 'custom') {
    return `${(pattern.dates || []).length} custom date${(pattern.dates || []).length > 1 ? 's' : ''} at ${pattern.time}`;
  }
  return 'Custom';
}